import { useState, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, FileSignature, Check } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import FarmlessIcon from "@/components/icons/farmless-icon";
import { SignaturePad } from "@/features/food-sensory/consent/signature-pad";

export const Route = createFileRoute("/consent/$share_code")({
  component: StandaloneConsentPage,
});

function StandaloneConsentPage() {
  const { share_code: shareCode } = Route.useParams();

  const [step, setStep] = useState<"identify" | "sign" | "done">("identify");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");

  if (step === "done") {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-olive-1">
        <div className="max-w-md w-full px-6 text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
            <Check className="h-6 w-6 text-green-600" />
          </div>
          <h1 className="text-lg font-semibold">Consent Signed</h1>
          <p className="text-sm text-muted-foreground">
            Thank you, {fullName}. Your consent has been recorded. You can
            close this page.
          </p>
        </div>
      </div>
    );
  }

  if (step === "sign") {
    return (
      <ConsentSignStep
        shareCode={shareCode}
        email={email}
        fullName={fullName}
        onComplete={() => setStep("done")}
      />
    );
  }

  return (
    <IdentifyStep
      shareCode={shareCode}
      email={email}
      setEmail={setEmail}
      fullName={fullName}
      setFullName={setFullName}
      onContinue={() => setStep("sign")}
    />
  );
}

// ─── Step 1: Identify ──────────────────────────────────────────────────────────

function IdentifyStep({
  shareCode,
  email,
  setEmail,
  fullName,
  setFullName,
  onContinue,
}: {
  shareCode: string;
  email: string;
  setEmail: (v: string) => void;
  fullName: string;
  setFullName: (v: string) => void;
  onContinue: () => void;
}) {
  const { data: template, isLoading, error } =
    trpc.documents.public.getTemplate.useQuery(shareCode);

  const [checking, setChecking] = useState(false);
  const utils = trpc.useUtils();

  const handleContinue = async () => {
    if (!email.trim() || !fullName.trim()) return;
    setChecking(true);
    try {
      const result = await utils.documents.public.checkConsent.fetch({
        share_code: shareCode,
        email: email.trim(),
      });
      if (result.signed) {
        toast.info("You have already signed this document.");
        return;
      }
      onContinue();
    } catch {
      toast.error("Failed to verify. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-sm font-medium text-destructive">
            Document not found
          </p>
          <p className="text-xs text-muted-foreground">
            This consent document may no longer be available.
          </p>
        </div>
      </div>
    );
  }

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canContinue = email.trim() && fullName.trim() && isValidEmail && !checking;

  return (
    <div className="min-h-[100dvh] bg-olive-1 px-6 py-10 sm:py-14">
      <div className="max-w-sm mx-auto space-y-8">
        <div className="text-center space-y-3">
          <FarmlessIcon className="h-10 w-10 mx-auto text-foreground/70" />
          <h1 className="text-2xl font-semibold tracking-tight">
            {template.name}
          </h1>
          <p className="text-sm text-muted-foreground">
            Please enter your details to proceed with signing.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="consent-fullname">Full Name</Label>
            <Input
              id="consent-fullname"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="consent-email">Email</Label>
            <Input
              id="consent-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
            />
          </div>
        </div>

        <Button
          className="w-full h-12 text-base"
          disabled={!canContinue}
          onClick={handleContinue}
        >
          {checking ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <FileSignature className="h-4 w-4 mr-2" />
          )}
          Continue
        </Button>
      </div>
    </div>
  );
}

// ─── Step 2: Sign ──────────────────────────────────────────────────────────────

function ConsentSignStep({
  shareCode,
  email,
  fullName,
  onComplete,
}: {
  shareCode: string;
  email: string;
  fullName: string;
  onComplete: () => void;
}) {
  const { data: template } = trpc.documents.public.getTemplate.useQuery(shareCode);
  const [signature, setSignature] = useState<string | null>(null);
  const today = format(new Date(), "dd MMMM yyyy");

  const signMutation = trpc.documents.public.sign.useMutation({
    onSuccess: () => onComplete(),
    onError: (err) => toast.error(err.message || "Failed to submit consent."),
  });

  const handleSignatureChange = useCallback((base64: string | null) => {
    setSignature(base64);
  }, []);

  const canSubmit = signature && !signMutation.isPending;

  if (!template) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div
      className="bg-olive-1 px-4 sm:px-6 py-8 sm:py-12"
      style={{ overflow: "auto", height: "100dvh" }}
    >
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <div className="mx-auto w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <FileSignature className="h-5 w-5 text-muted-foreground" />
          </div>
          <h1 className="text-xl font-semibold">{template.name}</h1>
          <p className="text-sm text-muted-foreground">
            Please read the document below carefully and sign to continue.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-8 py-8 sm:px-12 sm:py-10 prose prose-sm max-w-none consent-content">
            <div dangerouslySetInnerHTML={{ __html: template.content }} />
            <style>{`
              .consent-content p:empty { min-height: 1em; }
              .consent-content ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; }
              .consent-content ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
              .consent-content li { margin: 0.25em 0; }
              .consent-content li p { margin: 0; }
            `}</style>
          </div>

          <div className="border-t px-8 py-6 sm:px-12 space-y-5 bg-muted/10">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Full Name</Label>
              <p className="text-sm">{fullName}</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Email</Label>
              <p className="text-sm text-muted-foreground">{email}</p>
            </div>

            <SignaturePad onSignatureChange={handleSignatureChange} />

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Date</Label>
              <p className="text-sm text-muted-foreground">{today}</p>
            </div>
          </div>
        </div>

        <Button
          className="w-full h-12 text-base"
          disabled={!canSubmit}
          onClick={() =>
            signMutation.mutate({
              share_code: shareCode,
              email: email.trim(),
              full_name: fullName.trim(),
              signature: signature!,
            })
          }
        >
          {signMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <FileSignature className="h-4 w-4 mr-2" />
          )}
          Sign and Submit
        </Button>
      </div>
    </div>
  );
}
